<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import axios from "axios";
import { authHeader } from "../../../helpers/auth-header";

import ConfirmationModal from "@/components/modals/confirmation-modal";
import MessageModal from "@/components/modals/message-modal";

import InputGroup from "@/components/forms/input-group";
import SelectGroup from "@/components/forms/select-group";
import SaveButton from "@/components/buttons/save-button";

/**
 * Orders Component
 */
export default {
    components: {
        Layout,
        PageHeader,
        ConfirmationModal,
        MessageModal,

        InputGroup,
        SelectGroup,
        SaveButton
    },
    data() {
        const id = this.$route.params.id;
        const modeName = id === 'add' ? 'Add' : 'Edit';

        return {
            id: id,
            title: `Timeslot Category ${modeName}`,
            modeName,
            items: [
                {
                    text: "Timeslot Category"
                },
                {
                    text: modeName,
                    active: true,
                },
            ],

            tabs: ['info', 'timeslot'],

            data: {
                name: null,
                page_title: null,
                meta_keyword: null,
                meta_description: null,
                description: null,
                status: 1,
                sort_order: 0,
                weekdayTimeslots: [''],
                weekdayTimeslotInputs: [''],
                weekendTimeslots: [''],
                weekendTimeslotInputs: [''],
            },
            statusOptions: [
                { name: 'Active', value: 1 },
                { name: 'Inactive', value: 0 },
            ],

            errors: {},
        };
    },
    computed: {
        weekdayTimeslotInputs() {
            return this.data.weekdayTimeslots.slice();
        },
        weekendTimeslotInputs() {
            return this.data.weekendTimeslots.slice();
        },
    },
    async mounted() {        
        if (this.modeName !== 'Add') {
            const result = await axios.get(`${process.env.VUE_APP_APIURL}/timeslot-category/${this.id}`, {
                headers: authHeader(),
            });
            if (result && result.data['code'] === 'success') {
                const data = result.data['data']['category'];
                for (const [key, item] of Object.entries(data)) {
                    if (key in this.data) {
                        this.data[key] = item;
                    }
                }

                this.data.weekdayTimeslots = [];
                this.data.weekendTimeslots = [];
                const timeslotsData = JSON.parse(data.timeslot.timeslots);
                for (const [key, value] of Object.entries(timeslotsData)) {
                    if (key.startsWith('weekday_')) {
                        this.data.weekdayTimeslots.push(value);
                    } else if (key.startsWith('weekend_')) {
                        this.data.weekendTimeslots.push(value);
                    }
                }
            }
        }
    },
    methods: {
        async save() {
            this.errors = {};

            const modifiedWeekdayTimeslots = this.data.weekdayTimeslots.map((timeslot, index) => ({
                weekday: 'weekday_' + (index + 1),
                timeslot: this.formatTimeslot(timeslot),
            }));

            const modifiedWeekendTimeslots = this.data.weekendTimeslots.map((timeslot, index) => ({
                weekend: 'weekend_' + (index + 1),
                timeslot: this.formatTimeslot(timeslot),
            }));

            const requestData = {
                ...this.data,
                weekdayTimeslots: JSON.stringify(modifiedWeekdayTimeslots),
                weekendTimeslots: JSON.stringify(modifiedWeekendTimeslots),
            };

            let result;
            if (this.modeName === 'Add') {
                result = await axios.post(`${process.env.VUE_APP_APIURL}/timeslot-category`, requestData, {
                    headers: authHeader(),
                });
            } else {
                result = await axios.put(`${process.env.VUE_APP_APIURL}/timeslot-category/${this.id}`, requestData, {
                    headers: authHeader(),
                });                
            }

            if (result) {
                if (result.data['code'] === 'success') {
                    if (this.modeName === 'Add') {
                        this.$router.back();
                    } else {
                        this.$refs.messageModal.showModal('Your record has been updated successfully');
                    }
                } else if (result.data['code'] === 'invalid_field') {
                    this.errors = result.data['errors'];
                }
            }
        },

        addWeekdayTimeslot() {
            this.data.weekdayTimeslots.push('');
            this.data.weekdayTimeslotInputs.push('');
        },

        updateWeekdayTimeslot(value, index) {
            this.$set(this.data.weekdayTimeslots, index, value);
        },

        addWeekendTimeslot() {
            this.data.weekendTimeslots.push('');
            this.data.weekendTimeslotInputs.push('');
        },

        updateWeekendTimeslot(value, index) {
            this.$set(this.data.weekendTimeslots, index, value);
        },

        removeWeekdayTimeslot(index) {
            this.data.weekdayTimeslots.splice(index, 1);
            this.data.weekdayTimeslotInputs.splice(index, 1);
        },

        removeWeekendTimeslot(index) {
            this.data.weekendTimeslots.splice(index, 1);
            this.data.weekendTimeslotInputs.splice(index, 1);
        },

        formatTimeslot(timeslot) {
            if (/^\d{2}:\d{2}:\d{2}$/.test(timeslot)) {
                return timeslot;
            } else if (/^\d{2}:\d{2}$/.test(timeslot)) {
                return timeslot + ':00';
            } else {
                return '00:00:00';
            }
        },
    }
};
</script>

<template>
    <Layout>
        <PageHeader :title="title" :items="items" />
        <ConfirmationModal ref="confirmationModal" />
        <MessageModal ref="messageModal" />
        <div class="row">
            <div class="col-lg-12">
                <div class="card">
                    <div class="card-body p-3">
                        <b-tabs content-class="mt-3">
                            <b-tab title="Info" active>
                                <div class="row">
                                    <div class="col-12 col-md-5 col-lg-4">
                                        <InputGroup type="text" id="name" displayName="Name*" v-model="data.name" :error="errors.name" />
                                    </div>
                                    <div class="col-12 col-md-5 col-lg-4">
                                        <SelectGroup id="status" displayName="Status*" v-model="data.status" :error="errors.status" :options="statusOptions" />
                                    </div>
                                </div>
                            </b-tab>
                            <b-tab title="Timeslots">
                                <button class="add_timeslot btn btn-outline-secondary mb-3" @click="addWeekdayTimeslot">Add Weekday Timeslot</button>
                                <button class="add_timeslot btn btn-outline-secondary mb-3" @click="addWeekendTimeslot">Add Weekend Timeslot</button>
                                <div class="row">
                                    <div class="col-12" v-for="(timeslot, index) in data.weekdayTimeslots" :key="'weekday_' + index">
                                        <div class="row">
                                            <div v-if="index === 0" class="col-6 col-md-3">
                                                <label :for="`weekday_${index}`">Weekday Timeslots</label>
                                                <input type="time" class="form-control mb-2" :id="`weekday_${index}`" v-model="data.weekdayTimeslots[index]" @input="updateWeekdayTimeslot($event.target.value, index)" />
                                            </div>
                                            <div v-else class="col-6 col-md-3">
                                                <input type="time" class="form-control my-2" :id="`weekday_${index}`" v-model="data.weekdayTimeslots[index]" @input="updateWeekdayTimeslot($event.target.value, index)" />
                                            </div>
                                            <div class="col-6 col-md-3">
                                                <button v-if="index > 0" class="btn btn-danger w-100 mt-2" @click="removeWeekdayTimeslot(index)">Remove</button>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-12">
                                        <hr />
                                    </div>
                                    <div class="col-12" v-for="(timeslot, index) in data.weekendTimeslots" :key="'weekend_' + index">
                                        <div class="row">
                                            <div v-if="index === 0" class="col-6 col-md-3">
                                                <label :for="`weekend_${index}`">Weekend Timeslots</label>
                                                <input type="time" class="form-control mb-2" :id="`weekend_${index}`" v-model="data.weekendTimeslots[index]" @input="updateWeekendTimeslot($event.target.value, index)" />
                                            </div>
                                            <div v-else class="col-6 col-md-3">
                                                <input type="time" class="form-control my-2" :id="`weekend_${index}`" v-model="data.weekendTimeslots[index]" @input="updateWeekendTimeslot($event.target.value, index)" />
                                            </div>
                                            <div class="col-6 col-md-3">
                                                <button v-if="index > 0" class="btn btn-danger w-100 mt-2" @click="removeWeekendTimeslot(index)">Remove</button>
                                            </div>
                                            <!-- <div class="alert alert-danger mt-3" role="alert" v-if="errors.weekdayTimeslots && errors.weekdayTimeslots.length > 0"> -->
                                                <!-- {{ errors.weekendTimeslots[index] }} -->
                                            <!-- </div> -->
                                        </div>
                                    </div>
                                </div>
                            </b-tab>
                        </b-tabs>
                        <SaveButton @click="save" class="mt-5" />
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>